import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, HomeIcon, FileTextIcon, LandmarkIcon, KeyIcon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.8,
    ease: 'easeOut'
  }
};
const staggerContainer = {
  initial: {
    opacity: 0
  },
  whileInView: {
    opacity: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    staggerChildren: 0.2
  }
};
export function Conveyancing() {
  return <div className="w-full pt-24 bg-brand-offwhite" data-id="element-222">
      {/* Hero Section */}
      <section className="relative py-24 bg-brand-charcoal text-white overflow-hidden" data-id="element-223">
        <div className="absolute inset-0 z-0" data-id="element-224">
          <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&q=80&w=2000" alt="Luxury property" className="w-full h-full object-cover opacity-30" data-id="element-225" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal via-brand-charcoal/80 to-transparent" data-id="element-226"></div>
        </div>
        <div className="relative z-10 max-w-container mx-auto px-6" data-id="element-227">
          <div className="max-w-2xl" data-id="element-228">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-229">
              <span className="inline-block py-1 px-3 rounded-full bg-brand-gold/20 text-brand-gold text-sm font-semibold tracking-wider uppercase mb-6 border border-brand-gold/30" data-id="element-230">
                Specialized Service
              </span>
              <h1 className="font-serif text-5xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-231">
                Botswana's Property Transfer Specialists
              </h1>
              <p className="text-xl text-gray-300 mb-10 leading-relaxed" data-id="element-232">
                Expert conveyancing services ensuring your property transactions
                are handled with precision, efficiency, and absolute legal
                certainty.
              </p>
              <Link to="/contact" className="inline-block px-8 py-4 bg-brand-gold hover:bg-brand-gold-light text-white rounded-full font-medium transition-all shadow-lg" data-id="element-233">
                Start Your Transfer
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24" data-id="element-234">
        <div className="max-w-container mx-auto px-6" data-id="element-235">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-236">
            <motion.div {...fadeInUp} data-id="element-237">
              <h2 className="font-serif text-4xl font-bold text-brand-green mb-6" data-id="element-238">
                Navigating Property Law with Confidence
              </h2>
              <div className="w-20 h-1 bg-brand-gold mb-8" data-id="element-239"></div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-240">
                Buying or selling property is one of the most significant
                financial decisions you'll ever make. The legal processes
                involved can be complex and daunting without the right guidance.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-241">
                McErick Attorneys provides professional conveyancing services
                from the initial agreement through to the final registration of
                ownership at the Deeds Registry, ensuring every transaction is
                handled efficiently and accurately.
              </p>
              <ul className="space-y-4" data-id="element-242">
                {['Residential & Commercial Property Transfers', 'Registration of Mortgage Bonds', 'Sectional Title Developments', 'Subdivisions & Consolidations', 'Deeds Registry Searches & Advice'].map((item, i) => <li key={i} className="flex items-start" data-id="element-243">
                    <CheckCircle2Icon className="w-6 h-6 text-brand-gold mr-3 shrink-0 mt-0.5" data-id="element-244" />
                    <span className="text-gray-700 font-medium" data-id="element-245">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
            <motion.div {...fadeInUp} className="relative rounded-2xl overflow-hidden shadow-card h-[500px]" data-id="element-246">
              <img src="https://images.unsplash.com/photo-1560518883-ce09059eeefa?auto=format&fit=crop&q=80&w=1200" alt="Property keys handover" className="w-full h-full object-cover" data-id="element-247" />
            </motion.div>
          </div>

          {/* Process */}
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16" data-id="element-248">
            <h2 className="font-serif text-4xl font-bold text-brand-green mb-6" data-id="element-249">
              The Conveyancing Process
            </h2>
            <p className="text-lg text-gray-600" data-id="element-250">
              We guide you through every step of the property transfer journey.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-251">
            {[{
            icon: FileTextIcon,
            title: 'Agreement',
            desc: 'Drafting and reviewing the Deed of Sale to protect your interests.'
          }, {
            icon: LandmarkIcon,
            title: 'Financing',
            desc: 'Liaising with banks for bond approval and guarantees.'
          }, {
            icon: KeyIcon,
            title: 'Clearances',
            desc: 'Obtaining rates clearance certificates and transfer duty receipts.'
          }, {
            icon: HomeIcon,
            title: 'Registration',
            desc: 'Lodging documents at the Deeds Office for final registration.'
          }].map((step, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-white p-8 rounded-2xl shadow-soft border border-gray-100 text-center hover:-translate-y-2 transition-transform duration-300" data-id="element-252">
                <div className="w-16 h-16 mx-auto bg-brand-offwhite rounded-full flex items-center justify-center mb-6" data-id="element-253">
                  <step.icon className="w-8 h-8 text-brand-green" data-id="element-254" />
                </div>
                <h3 className="font-serif text-xl font-bold text-brand-charcoal mb-3" data-id="element-255">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-256">{step.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>
    </div>;
}